import React from 'react';

export function Button({ children, variant = 'primary', size = 'md', className = '', disabled = false, style = {}, ...props }) {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'text-white shadow-sm hover:shadow',
    secondary: 'bg-[var(--badge-default-bg)] text-[var(--text-heading)] hover:opacity-80 focus:ring-[var(--border)]',
    outline: 'border border-current bg-transparent hover:bg-current/5',
    ghost: 'text-[var(--muted-foreground)] hover:bg-[var(--hover-bg)] hover:text-[var(--text-heading)]',
    danger: 'bg-[#DC2626] text-white hover:bg-[#B91C1C] focus:ring-[#DC2626] shadow-sm',
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-sm',
  };

  const mergedStyle = variant === 'primary'
    ? { backgroundColor: 'var(--module-color, #2563EB)', ...style }
    : variant === 'outline'
    ? { color: 'var(--module-color, #2563EB)', borderColor: 'var(--module-color, #2563EB)', ...style }
    : style;

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      style={mergedStyle}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ label, error, className = '', ...props }) {
  return (
    <div className={`space-y-1 ${className}`}>
      {label && <label className="block text-sm font-medium text-[var(--text-heading)]">{label}</label>}
      <input
        className={`w-full px-3 py-2 border rounded-md transition-all duration-200 text-sm text-[var(--foreground)]
          ${error ? 'border-[#DC2626] focus:ring-[#DC2626]' : 'border-[var(--input-border)] focus:border-[color:var(--module-color,#2563EB)] focus:ring-[color:var(--module-color,#2563EB)]'}
          focus:outline-none focus:ring-2 focus:ring-opacity-20 bg-[var(--input-bg)]`}
        {...props}
      />
      {error && <p className="text-sm text-[#DC2626]">{error}</p>}
    </div>
  );
}

export function Select({ label, error, children, className = '', ...props }) {
  return (
    <div className={`space-y-1 ${className}`}>
      {label && <label className="block text-sm font-medium text-[var(--text-heading)]">{label}</label>}
      <select
        className={`w-full px-3 py-2 border rounded-md transition-all duration-200 text-sm text-[var(--foreground)]
          ${error ? 'border-[#DC2626]' : 'border-[var(--input-border)] focus:border-[color:var(--module-color,#2563EB)]'}
          focus:outline-none focus:ring-2 focus:ring-[color:var(--module-color,#2563EB)] focus:ring-opacity-20 bg-[var(--input-bg)]`}
        {...props}
      >
        {children}
      </select>
      {error && <p className="text-sm text-[#DC2626]">{error}</p>}
    </div>
  );
}

export function Card({ children, className = '', ...props }) {
  return (
    <div className={`rounded-lg border ${className}`}
      style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)', boxShadow: 'var(--card-shadow)' }}
      {...props}
    >
      {children}
    </div>
  );
}

export function Badge({ children, variant = 'default', className = '' }) {
  const variants = {
    default: 'bg-[var(--badge-default-bg)] text-[var(--badge-default-text)]',
    primary: 'text-white',
    success: 'text-[var(--status-active)]',
    warning: 'text-[#D97706]',
    danger: 'text-[var(--accent-red)]',
  };

  const bgOverrides = {
    success: 'var(--success-bg)',
    warning: 'var(--warning-bg)',
    danger: 'var(--danger-bg)',
  };

  const badgeStyle = variant === 'primary'
    ? { backgroundColor: 'color-mix(in srgb, var(--module-color, #2563EB) 12%, transparent)', color: 'var(--module-color, #2563EB)' }
    : bgOverrides[variant] ? { backgroundColor: bgOverrides[variant] } : {};

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${variants[variant]} ${className}`}
      style={badgeStyle}
    >
      {children}
    </span>
  );
}

export function Modal({ isOpen, open, onClose, title, children }) {
  // Support both 'isOpen' and 'open' props for backwards compatibility
  if (!isOpen && !open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="modal">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity" onClick={onClose} />
        <div className="relative rounded-xl shadow-xl max-w-lg w-full p-6" style={{ backgroundColor: 'var(--modal-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-heading)]">{title}</h3>
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-heading)] transition-colors p-1 rounded-lg hover:bg-[var(--hover-bg)]">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Table({ columns, data, onRowClick }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b" style={{ borderColor: 'var(--card-border)' }}>
            {columns.map((col, idx) => (
              <th key={idx} className="px-4 py-3 text-left text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y" style={{ '--tw-divide-color': 'var(--table-divider)' }}>
          {data.map((row, rowIdx) => (
            <tr 
              key={rowIdx} 
              className={`hover:bg-[var(--hover-bg)] transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col, colIdx) => (
                <td key={colIdx} className="px-4 py-3 text-sm text-[var(--text-heading)]">
                  {col.render ? col.render(row) : row[col.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8 text-[var(--text-muted)]">No data available</div>
      )}
    </div>
  );
}

export function Spinner({ size = 'md' }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return (
    <div
      className={`${sizes[size]} border-2 rounded-full animate-spin`}
      style={{ borderColor: 'var(--card-border)', borderTopColor: 'var(--module-color, #2563EB)' }}
    />
  );
}

export function Tabs({ tabs, activeTab, onChange }) {
  return (
    <div className="border-b" style={{ borderColor: 'var(--card-border)' }}>
      <nav className="flex space-x-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`py-2.5 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? 'text-[color:var(--module-color,#2563EB)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-heading)] hover:border-[var(--border)]'
            }`}
            style={activeTab === tab.id ? { borderBottomColor: 'var(--module-color, #2563EB)' } : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
